// import { logError } from '../../modal/logging/logger';
// import { trackError, trackEvent } from '../tracking/tracking';
// import trelloIFrame from './trello-iframe';
import CustomError from "./custom-error";
import axios, { AxiosError, AxiosRequestConfig, Method } from "axios";
import useIframe from "../_use-iframe";
// import axiosRetry from 'axios-retry';

const API_KEY = process.env.API_KEY;
const API_BASE_URL = process.env.API_BASE_URL;

export type TTrelloClient = {
  // trelloIFrameArgs: (arg: string) => string,
  trelloRestAPI(
    method: Method,
    partialUrl: string,
    query?: Record<string, unknown>,
    quietErrors?: (new (...args: any[]) => CustomAPICallError)[],
    body?: object
  ): Promise<any>;
};

const useRestApiClient = () => {
  const trelloIFrame = useIframe();

  const trelloClient: Promise<TTrelloClient> = new Promise((resolve) => {
    trelloIFrame
      .getRestApi()
      .getToken()
      .then((token) => {
        if (!token) {
          // return reject("Could not find token");
          return {
            trelloRestAPI: () => {
              throw new CustomError(
                "TrelloClient not initialized - Could not find token"
              );
            },
          };
        }
        return resolve({
          // trelloIFrameArgs: environment.trelloIFrameArgs,
          trelloRestAPI: (method, partialUrl, query, quietErrors, body) =>
            trelloRestAPI(
              method,
              partialUrl,
              query,
              quietErrors ?? [],
              body ?? {},
              token
            ),
        });
      });
  });

  return trelloClient;
};

export default useRestApiClient;

const trelloRestAPI = (
  method: Method,
  partialUrl: string,
  query = {},
  quietErrors: (new (...args: any[]) => CustomAPICallError)[],
  body: object,
  token: string
): Promise<any> => {
  // in Production we are already logged in when we reach modal, so no need for authorization
  // console.log(API_BASE_URL + partialUrl);
  const url = new URL(API_BASE_URL + partialUrl);
  url.search = new URLSearchParams({ ...query }).toString();
  const client = getClient(method, token);
  return client({ url: url.toString(), data: body })
    .then((resp) => resp.data)
    .catch((error: AxiosError) => {
      if (error.response) {
        const bodyAsText = error.response.data;

        if (
          error.response.status == 403 &&
          bodyAsText.includes("Too many checklists")
        ) {
          throw new TooManyChcecklistsOnBoardsCardsError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(TooManyChcecklistsOnBoardsCardsError)
          );
        }
        if (
          error.response.status == 429 &&
          bodyAsText.includes("LOCK_TAKEN_ERROR")
        ) {
          throw new ResourceLockedAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(ResourceLockedAPICallError)
          );
        }
        if (error.response.status == 429) {
          throw new RequestLimitReachedOnAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(RequestLimitReachedOnAPICallError)
          );
        } else if (
          error.response.status == 401 &&
          bodyAsText.includes("unauthorized card permission requested")
        ) {
          throw new UnauthorizedCardPermissionRequestedAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(
              UnauthorizedCardPermissionRequestedAPICallError
            )
          );
        } else if (error.response.status == 401) {
          throw new UnauthorizedAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(UnauthorizedAPICallError)
          );
        } else if (
          error.response.status == 404 &&
          bodyAsText.includes("card not found")
        ) {
          throw new CardNotFoundAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(CardNotFoundAPICallError)
          );
        } else if (
          error.response.status == 400 &&
          (bodyAsText.includes(
            "Board must be in a team — specify an idOrganization"
          ) ||
            bodyAsText.includes(
              "ENTERPRISE_USER_CANNOT_CREATE_WORKSPACE_FOR_BOARD"
            ))
        ) {
          throw new CouldNotCreateBoardAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(CouldNotCreateBoardAPICallError)
          );
        } else if (
          error.response.status == 400 &&
          bodyAsText.includes("invalid id")
        ) {
          throw new InvalidIdAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(InvalidIdAPICallError)
          );
        } else if (
          error.response.status == 404 &&
          bodyAsText.includes("The requested resource was not found.")
        ) {
          throw new ResourceNotFoundAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(ResourceNotFoundAPICallError)
          );
        } else {
          throw new UnknownAPICallError(
            error.response.status,
            url,
            method,
            bodyAsText,
            quietErrors.includes(UnknownAPICallError)
          );
        }
      } else {
        const errorCode = parseInt(error.code ?? "");
        throw new UnknownAPICallError(
          isNaN(errorCode) ? 0 : errorCode,
          url,
          method,
          error.message,
          quietErrors.includes(UnknownAPICallError)
        );
      }
    });
};

export class CustomAPICallError extends Error {
  statusCode: number;
  url: string;
  body: string;
  constructor(
    errorType: string,
    statusCode: number,
    url: URL,
    method: string,
    message: string,
    body: string,
    suppressLogAndTracking: boolean
  ) {
    super(message);
    this.statusCode = statusCode;
    this.url = url.href;
    this.body;
    if (!suppressLogAndTracking) {
      throw new Error(message);
      // logError(message, { statusCode, url: url.href, method, body });
      // throw { message, data: { statusCode, url: url.href, method, body } };
      // trackError(errorType, { category: "TrelloAPICall", statusCode, url: url.href, method, message, body })
    }
  }
}

export class UnauthorizedCardPermissionRequestedAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "UnauthorizedCardPermissionRequested",
      statusCode,
      url,
      method,
      "Unauthorized card permission requested",
      body,
      suppressLogAndTracking
    );
  }
}
export class UnauthorizedAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "UnauthorizedAPICall",
      statusCode,
      url,
      method,
      "Unauthorized API Call",
      body,
      suppressLogAndTracking
    );
  }
}

export class RequestLimitReachedOnAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "RequestLimitReachedOnAPICall",
      statusCode,
      url,
      method,
      "Request Limit has been reached",
      body,
      suppressLogAndTracking
    );
  }
}

export class ResourceLockedAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "ResourceLockedOnAPICall",
      statusCode,
      url,
      method,
      `Resource Locked on API Call`,
      body,
      suppressLogAndTracking
    );
  }
}

export class TooManyChcecklistsOnBoardsCardsError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "TooManyChcecklistsOnBoardsCards",
      statusCode,
      url,
      method,
      `Too many checklists on cards in the board. Observed when calling nested card resource with "checkItemStates" not excluded`,
      body,
      suppressLogAndTracking
    );
  }
}

export class CardNotFoundAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "CardNotFound",
      statusCode,
      url,
      method,
      `Card was not found`,
      body,
      suppressLogAndTracking
    );
  }
}

export class CouldNotCreateBoardAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "CouldNotCreateBoard",
      statusCode,
      url,
      method,
      `Could not create board`,
      body,
      suppressLogAndTracking
    );
  }
}

export class InvalidIdAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "InvalidId",
      statusCode,
      url,
      method,
      `Not a properly formatted Id`,
      body,
      suppressLogAndTracking
    );
  }
}
export class ResourceNotFoundAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "ResourceNotFound",
      statusCode,
      url,
      method,
      `The requested resource was not found`,
      body,
      suppressLogAndTracking
    );
  }
}

export class UnknownAPICallError extends CustomAPICallError {
  constructor(
    statusCode: number,
    url: URL,
    method: string,
    body = "",
    suppressLogAndTracking: boolean
  ) {
    super(
      "UnknownAPICallError",
      statusCode,
      url,
      method,
      "Unknown API call error",
      body,
      suppressLogAndTracking
    );
  }
}

function getClient(method: Method, token: string) {
  const opts: AxiosRequestConfig = {
    method: method,
    headers: {
      Authorization: `OAuth oauth_consumer_key="${API_KEY}", oauth_token="${token}"`,
      "Content-Type": "application/json",
    },
    // data: method == 'put' ? JSON.stringify(body) : undefined,
  };
  const client = axios.create(opts);
  // axiosRetry(client, {
  // 	retries: 3, retryDelay: (retryCount) => {
  // 		// Appox at 2sec, 4sec and 8sec
  // 		const expDelay = (Math.pow(2, retryCount) + Math.random()) * 1000;
  // 		trackEvent("RetryTrelloRestCallWithAxiosRetry", { retry: retryCount })
  // 		// console.log(expDelay, retryCount);
  // 		return expDelay;
  // 	},
  // 	retryCondition: (err) => {
  // 		// Retry if Networ or Idempotent with 5xx or Request Limit Reached
  // 		return axiosRetry.isNetworkOrIdempotentRequestError(err) || err.response?.status == 429;
  // 	}
  // });
  return client;
}
